import {nanoid} from 'nanoid';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import {safeReorderArray} from '@netx/core-next/utils/array/safeReorderArray';
import {ACTIONS} from '../constants';
import {Action, Items} from './actions';

const logger = getPrefixedLogger('itemManager/store');

type State = {
	count: number;
	hasMaxItems: boolean;
	items: Items;
	lastAction: null | 'add' | 'remove' | 'reorder';
	lastId: null | number | string;
	maxItems: number;
};

export const initialState: State = {
	count: 0,
	hasMaxItems: false,
	items: [],
	lastAction: null,
	lastId: null,
	maxItems: Infinity,
};

const limitItems = (items: Items, limit: number) => {
	if (items.length <= limit) return items;
	return items.slice(0, limit);
};

const onRemove = (items: Items, id: number | string) =>
	items.filter((item) => item !== id);

export default (state: State, action: Action): State => {
	logger.silly(action.type);

	switch (action.type) {
		case ACTIONS.ITEM_MANAGER_ADD: {
			const id = action.data || nanoid();
			const items = [...state.items, id];
			const count = items.length;
			return {
				...state,
				count,
				hasMaxItems: count === state.maxItems,
				items,
				lastAction: 'add',
				lastId: id,
			};
		}
		case ACTIONS.ITEM_MANAGER_ADD_MULTIPLE: {
			const items = limitItems(
				[...state.items, ...action.data],
				state.maxItems,
			);
			const count = items.length;
			return {
				...state,
				count,
				hasMaxItems: count === state.maxItems,
				lastAction: 'add',
				items,
			};
		}
		case ACTIONS.ITEM_MANAGER_REMOVE: {
			const items = onRemove(state.items, action.data);
			const count = items.length;
			return {
				...state,
				count,
				hasMaxItems: count === state.maxItems,
				items,
				lastAction: 'remove',
			};
		}
		case ACTIONS.ITEM_MANAGER_REORDER: {
			const {fromIndex, toIndex} = action.data;
			return {
				...state,
				items: safeReorderArray(state.items, fromIndex, toIndex),
				lastAction: 'reorder',
			};
		}
		case ACTIONS.ITEM_MANAGER_RESET: {
			if (action.data) {
				const items = limitItems(action.data, state.maxItems);
				const count = items.length;
				return {
					...initialState,
					count,
					hasMaxItems: count === state.maxItems,
					items,
					maxItems: state.maxItems,
				};
			}
			return {
				...initialState,
				maxItems: state.maxItems,
			};
		}
		case ACTIONS.ITEM_MANAGER_SET_MAX_ITEMS: {
			const maxItems = Math.max(action.data || Infinity, 0);
			if (state.count > maxItems) {
				return {
					...state,
					count: maxItems,
					hasMaxItems: true,
					items: limitItems(state.items, maxItems),
					maxItems,
				};
			}
			return {
				...state,
				hasMaxItems: state.count === maxItems,
				maxItems,
			};
		}
	}

	return state;
};
